import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { MapContainer, TileLayer, useMap, CircleMarker, Tooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import api from '../services/api';
import azureApi, { getLugares, getMunicipios, getDepartamentos } from '../services/azureApi';

// ── Helper: mapea TipoServicio/ClaseServicio de Azure al enum local ────────────
function mapearTipoIncidente(tipoServicio = '', claseServicio = '') {
  const t = (tipoServicio + ' ' + claseServicio).toLowerCase();
  if (t.includes('incendio') || t.includes('conato') || t.includes('forestal')) return 'incendio';
  if (t.includes('rescate') || t.includes('barranco') || t.includes('extraviad') || t.includes('rastreo')) return 'rescate';
  if (t.includes('tránsito') || t.includes('transito') || t.includes('motorista') ||
      t.includes('atropellad') || t.includes('vehículo') || t.includes('vehiculo') ||
      t.includes('aéreo') || t.includes('aereo')) return 'accidente';
  if (t.includes('material') || t.includes('quím') || t.includes('quim') || t.includes('derrame')) return 'material_peligroso';
  if (t.includes('ambulancia') || t.includes('traslado') || t.includes('médica') ||
      t.includes('medica') || t.includes('caída') || t.includes('caida') || t.includes('fallecid')) return 'ambulancia';
  return 'otro';
}

// ── Constants ─────────────────────────────────────────────────────────────────
const TIPOS_INCIDENTE = [
  { value: 'incendio',           label: 'Incendio',           icon: '🔥' },
  { value: 'rescate',            label: 'Rescate',            icon: '🪢' },
  { value: 'accidente',          label: 'Accidente vial',     icon: '🚗' },
  { value: 'material_peligroso', label: 'Material peligroso', icon: '☢️' },
  { value: 'ambulancia',         label: 'Ambulancia',         icon: '🚑' },
  { value: 'otro',               label: 'Otro',               icon: '📋' },
];

const NIVEL = {
  bajo:    { color:'#10b981', bg:'#ecfdf5', border:'#6ee7b7', label:'BAJO',    emoji:'✅', desc:'Operación normal · Sin acción especial',              order:0 },
  medio:   { color:'#f59e0b', bg:'#fffbeb', border:'#fcd34d', label:'MEDIO',   emoji:'⚠️', desc:'Monitoreo preventivo · 1 unidad standby',              order:1 },
  alto:    { color:'#f97316', bg:'#fff7ed', border:'#fdba74', label:'ALTO',    emoji:'🚨', desc:'Alerta operacional · 2+ unidades en alerta',           order:2 },
  critico: { color:'#ef4444', bg:'#fef2f2', border:'#fca5a5', label:'CRÍTICO', emoji:'🔴', desc:'Emergencia máxima · Activar todas las unidades',       order:3 },
};

// Colores RGB para el canvas del heatmap según nivel de predicción
const NIVEL_RGB = {
  bajo:    { r: 16,  g: 185, b: 129 },
  medio:   { r: 245, g: 158, b: 11  },
  alto:    { r: 249, g: 115, b: 22  },
  critico: { r: 239, g: 68,  b: 68  },
};

const DIAS  = ['Lunes','Martes','Miércoles','Jueves','Viernes','Sábado','Domingo'];
const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const TIPO_COLOR = { incendio:'#ef4444', rescate:'#3b82f6', accidente:'#f59e0b', ambulancia:'#10b981', material_peligroso:'#8b5cf6', otro:'#64748b' };

const CLASE_ICONS = {
  'Accidentes Aéreos':              '✈️',  'Accidentes de Motorista':        '🛵',
  'Accidentes de Trabajo':          '⚠️',  'Accidentes de Tránsito':         '🚗',
  'Caidas Casuales':                '🏥',  'Caídas Casuales':                '🏥',
  'Conatos de Incendios':           '🔥',  'Control de Enjambres':           '🐝',
  'Enfermedad Común':               '🩺',  'Fallecidos por Diversas Causas': '📋',
  'Falsas Llamadas':                '📵',  'Fetos Localizados':              '🍼',
  'Hechos de Violencia':            '⚡',  'Incendios':                      '🔥',
  'Incendios en Vehículos':         '🚒',  'Incendios Forestales':           '🌲',
  'Otros':                          '📋',  'Personas Agredidas':             '🩹',
  'Personas Atropelladas':          '🚨',  'Personas Electrocutadas':        '⚡',
  'Personas Extraviadas':           '🆘',  'Personas Intoxicadas':           '💊',
  'Personas Mordidas por Animales': '🐾',  'Personas Quemadas':              '🔥',
  'Prevenciones':                   '🛡️',  'Rastreos Efectuados':            '🆘',
  'Recién Nacidos Abandonados':     '🍼',  'Rescates Acuáticos':             '🆘',
  'Rescates con Equipo Especial':   '🆘',  'Rescates de Animales':           '🐾',
  'Rescates en Barrancos':          '⛰️',  'Servicios de Agua':              '💧',
  'Servicios de Maternidad':        '🤰',  'Servicios varios':               '📋',
  'Traslado':                       '🚑',  'Viviendas Inundadas':            '🌊',
  'Viviendas Soterradas':           '🏚️',
};

// ── Municipio coordinates (mapeado por nombre de municipio) ───────────────────
const MUNICIPIO_COORDS_BY_NAME = {
  'JALAPA': { lat: 14.6315008, lon: -89.9934525 },
  'MONJAS': { lat: 14.5067877, lon: -89.9495102 },
  'MATAQUESCUINTLA': { lat: 14.526645, lon: -90.1913382 },
  'SAN CARLOS ALZATATE': { lat: 14.4971838, lon: -90.0610686 },
  'SAN LUIS JILOTEPEQUE': { lat: 14.6474095, lon: -89.7386026 },
  'SAN MANUEL CHAPARRÓN': { lat: 14.5216389, lon: -89.7728491 },
  'SAN MANUEL CHAPARRON': { lat: 14.5216389, lon: -89.7728491 }, // Sin acento
  'SAN PEDRO PINULA': { lat: 14.6646972, lon: -89.8486162 },
};

function getNow() {
  const n = new Date();
  return {
    hora:       n.getHours(),
    dia_semana: n.getDay() === 0 ? 6 : n.getDay() - 1,
    mes:        n.getMonth() + 1,
  };
}

// ── Risk Distribution Card ────────────────────────────────────────────────────
function RiskCard({ nivel, pct, isTop, animate }) {
  const cfg  = NIVEL[nivel];
  const barW = animate ? pct : 0;

  if (isTop) {
    return (
      <div style={{
        background:`linear-gradient(135deg,#0f172a 0%,#1a2540 60%,${cfg.color}14 100%)`,
        border:`2px solid ${cfg.color}`,
        borderRadius:'18px',
        padding:'22px 26px',
        position:'relative',
        overflow:'hidden',
        boxShadow:`0 12px 40px ${cfg.color}45, 0 4px 16px rgba(0,0,0,0.22)`,
      }}>
        <div style={{ position:'absolute',top:'-40px',right:'-40px',width:'160px',height:'160px',borderRadius:'50%',background:`${cfg.color}10`,pointerEvents:'none' }} />
        <div style={{ display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:'16px' }}>
          <div>
            <div style={{ display:'flex',alignItems:'center',gap:'10px',marginBottom:'10px' }}>
              <span style={{ fontSize:'2rem',lineHeight:1,filter:`drop-shadow(0 0 10px ${cfg.color}80)` }}>{cfg.emoji}</span>
              <div>
                <div style={{ fontSize:'.62rem',color:cfg.color,textTransform:'uppercase',letterSpacing:'.12em',fontWeight:800,marginBottom:'2px' }}>
                  NIVEL DETECTADO
                </div>
                <div style={{ fontSize:'1.4rem',fontWeight:900,color:'#fff',letterSpacing:'-.02em',lineHeight:1 }}>{cfg.label}</div>
              </div>
            </div>
            <div style={{ color:'rgba(255,255,255,0.5)',fontSize:'.8rem',lineHeight:1.5,marginBottom:'18px' }}>{cfg.desc}</div>
          </div>
          <div style={{ textAlign:'right',flexShrink:0 }}>
            <div style={{ fontSize:'3.5rem',fontWeight:900,color:cfg.color,lineHeight:1,letterSpacing:'-.04em' }}>{pct}%</div>
            <div style={{ fontSize:'.65rem',color:'rgba(255,255,255,0.3)',marginTop:'3px',textTransform:'uppercase',letterSpacing:'.08em' }}>probabilidad</div>
          </div>
        </div>
        <div style={{ background:'rgba(255,255,255,0.1)',borderRadius:'99px',height:'7px',overflow:'hidden' }}>
          <div style={{
            width:`${barW}%`,height:'7px',borderRadius:'99px',
            background:`linear-gradient(90deg,${cfg.color},${cfg.color}cc)`,
            transition:'width 1.3s cubic-bezier(.4,0,.2,1)',
            boxShadow:`0 0 12px ${cfg.color}80`,
          }} />
        </div>
      </div>
    );
  }

  return (
    <div style={{
      background:'#fff',
      border:`1.5px solid ${cfg.border}`,
      borderLeft:`4px solid ${cfg.color}`,
      borderRadius:'14px',
      padding:'14px 16px',
      boxShadow:'0 1px 6px rgba(0,0,0,0.05)',
    }}>
      <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',gap:'8px' }}>
        <div style={{ display:'flex',alignItems:'center',gap:'8px' }}>
          <span style={{ fontSize:'1.2rem',lineHeight:1 }}>{cfg.emoji}</span>
          <div>
            <div style={{ fontSize:'.84rem',fontWeight:800,color:'#1e293b' }}>{cfg.label}</div>
            <div style={{ fontSize:'.67rem',color:'#94a3b8',marginTop:'1px',lineHeight:1.3 }}>{cfg.desc}</div>
          </div>
        </div>
        <span style={{ fontSize:'1.6rem',fontWeight:900,color:cfg.color,flexShrink:0,lineHeight:1 }}>{pct}%</span>
      </div>
      <div style={{ background:'#f1f5f9',borderRadius:'99px',height:'4px',overflow:'hidden',marginTop:'11px' }}>
        <div style={{
          width:`${barW}%`,height:'4px',borderRadius:'99px',
          background:cfg.color,
          transition:'width 1.3s cubic-bezier(.4,0,.2,1)',
        }} />
      </div>
    </div>
  );
}

RiskCard.propTypes = {
  nivel:   PropTypes.string.isRequired,
  pct:     PropTypes.number.isRequired,
  isTop:   PropTypes.bool.isRequired,
  animate: PropTypes.bool.isRequired,
};

// ── Map Center Controller (actualiza el centro del mapa cuando cambia municipio) ──
function MapCenterController({ center, zoom }) {
  const map = useMap();
  
  useEffect(() => {
    if (map && center) {
      map.setView([center.lat, center.lon], zoom || map.getZoom(), { animate: true, duration: 0.8 });
    }
  }, [center, zoom, map]);
  
  return null;
}

MapCenterController.propTypes = {
  center: PropTypes.shape({
    lat: PropTypes.number,
    lon: PropTypes.number,
  }),
  zoom: PropTypes.number,
};

// ── Canvas Heatmap Layer ──────────────────────────────────────────────────────
function HeatmapLayer({ data, selectedMunicipio, resultado }) {
  const map = useMap();
  const layerRef    = useRef(null);
  const selectedRef = useRef(selectedMunicipio);
  const dataRef     = useRef(data);
  const resultadoRef = useRef(resultado);

  // Función de dibujo que siempre usa las refs (sin depender de closures del efecto)
  const draw = useCallback(() => {
    const overlay = layerRef.current;
    if (overlay && overlay._map) overlay._update();
  }, []);

  // Cuando cambia la selección: actualiza ref y redibuja sin destruir el canvas
  useEffect(() => {
    selectedRef.current = selectedMunicipio;
    draw();
  }, [selectedMunicipio, draw]);

  // Cuando cambia data: actualiza ref y redibuja
  useEffect(() => {
    dataRef.current = data;
    draw();
  }, [data, draw]);

  // Cuando llega un nuevo resultado de predicción: actualiza ref y redibuja
  useEffect(() => {
    resultadoRef.current = resultado;
    draw();
  }, [resultado, draw]);

  // Crea el layer UNA SOLA VEZ cuando el mapa está listo
  useEffect(() => {
    if (!map) return;

    const canvas = document.createElement('canvas');
    canvas.style.cssText = 'position:absolute;z-index:2;pointer-events:none;';

    const CanvasOverlay = L.Layer.extend({
      onAdd(m) {
        this._map = m;
        m.getPanes().overlayPane.appendChild(canvas);
        this._update();
        m.on('moveend zoomend resize', this._update, this);
      },
      onRemove(m) {
        if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
        m.off('moveend zoomend resize', this._update, this);
      },
      _update() {
        if (!canvas || !this._map) return;
        const d   = dataRef.current;
        const sel = selectedRef.current;
        const res = resultadoRef.current;
        if (!d || d.length === 0) return;

        const size = this._map.getSize();
        const nw   = this._map.latLngToContainerPoint(this._map.getBounds().getNorthWest());
        canvas.width  = size.x;
        canvas.height = size.y;
        canvas.style.left = nw.x + 'px';
        canvas.style.top  = nw.y + 'px';

        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        const maxVal = Math.max(...d.map(z => z.total_incidentes || 0), 1);
        const minVal = Math.min(...d.filter(z => z.total_incidentes > 0).map(z => z.total_incidentes), maxVal);
        const hasSel = !!sel;

        // Dibuja no-seleccionados primero, seleccionado encima
        const sorted = [...d].sort((a, b) => (a.municipio === sel ? 1 : 0) - (b.municipio === sel ? 1 : 0));

        sorted.forEach(z => {
          if (!z.lat || !z.lon) return;
          const isSel = hasSel && z.municipio === sel;
          try {
            const pt = this._map.latLngToContainerPoint([z.lat, z.lon]);

            const logMax = Math.log(maxVal + 1);
            const logMin = Math.log(minVal);
            const logVal = Math.log(Math.max(z.total_incidentes, minVal) + 1);
            const rawInt = maxVal === minVal ? 0.8 : Math.max(0.15, (logVal - logMin) / (logMax - logMin));
            const intens = isSel ? Math.min(1, rawInt * 1.3) : rawInt;

            // Si hay predicción y este es el municipio seleccionado, usar el color del nivel predicho
            let r, g, b;
            if (isSel && res && NIVEL_RGB[res.riesgo]) {
              const rgb = NIVEL_RGB[res.riesgo];
              r = rgb.r; g = rgb.g; b = rgb.b;
            } else if (intens < 0.25)       { r=34;  g=197; b=94;  }
            else if (intens < 0.45)  { const t=(intens-0.25)/0.2; r=Math.round(34+(245-34)*t); g=197; b=Math.round(94+(11-94)*t); }
            else if (intens < 0.6)   { r=245; g=158; b=11;  }
            else if (intens < 0.75)  { r=249; g=115; b=22;  }
            else if (intens < 0.88)  { r=239; g=68;  b=68;  }
            else                     { r=185; g=28;  b=28;  }

            const opBase  = hasSel ? (isSel ? (res ? Math.min(1, intens * 1.4) : intens) : intens * 0.12) : intens * 0.82;
            const radius  = isSel && res ? 160 : isSel ? 130 : 68;
            const numLays = isSel && res ? 9   : isSel ? 7   : 4;

            for (let lay = numLays; lay >= 0; lay--) {
              const r2   = radius - lay * (radius / (numLays + 1));
              const op   = opBase / (lay * 0.55 + 0.8);
              const grad = ctx.createRadialGradient(pt.x, pt.y, 0, pt.x, pt.y, r2);
              grad.addColorStop(0, `rgba(${r},${g},${b},${Math.min(1, op)})`);
              grad.addColorStop(1, `rgba(${r},${g},${b},0)`);
              ctx.fillStyle = grad;
              ctx.beginPath();
              ctx.arc(pt.x, pt.y, r2, 0, Math.PI * 2);
              ctx.fill();
            }

            if (isSel) {
              ctx.strokeStyle = `rgba(${r},${g},${b},0.95)`;
              ctx.lineWidth   = 3;
              ctx.beginPath(); ctx.arc(pt.x, pt.y, 18, 0, Math.PI*2); ctx.stroke();
              ctx.fillStyle   = `rgba(${r},${g},${b},1)`;
              ctx.beginPath(); ctx.arc(pt.x, pt.y, 7, 0, Math.PI*2); ctx.fill();

              ctx.font = 'bold 13px Inter,system-ui,sans-serif';
              ctx.textAlign = 'center';
              const tw = ctx.measureText(z.municipio).width;
              // Mostrar etiqueta de predicción si hay resultado
              if (res && NIVEL[res.riesgo]) {
                const nv = NIVEL[res.riesgo];
                const predLabel = `${nv.emoji} ${nv.label}`;
                const pw = ctx.measureText(predLabel).width;
                ctx.fillStyle = `rgba(${r},${g},${b},0.95)`;
                ctx.beginPath();
                ctx.roundRect(pt.x - pw/2 - 8, pt.y - 68, pw + 16, 22, 6);
                ctx.fill();
                ctx.fillStyle = '#fff';
                ctx.fillText(predLabel, pt.x, pt.y - 52);
              }
              ctx.fillStyle = 'rgba(15,23,42,0.85)';
              ctx.beginPath();
              ctx.roundRect(pt.x - tw/2 - 8, pt.y - 44, tw + 16, 22, 6);
              ctx.fill();
              ctx.fillStyle = '#fff';
              ctx.fillText(z.municipio, pt.x, pt.y - 28);
              ctx.textAlign = 'left';
            }
          } catch (_) { /* ignorar errores de proyección */ }
        });
      },
    });

    layerRef.current = new CanvasOverlay();
    layerRef.current.addTo(map);

    return () => { if (layerRef.current) { map.removeLayer(layerRef.current); layerRef.current = null; } };
  // Solo se crea/destruye cuando cambia el mapa — data y selection usan refs
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  return null;
}

HeatmapLayer.propTypes = {
  data: PropTypes.array,
  selectedMunicipio: PropTypes.string,
  resultado: PropTypes.object,
};

// ── Main Component ────────────────────────────────────────────────────────────
export default function PrediccionIA() {
  const [form, setForm]           = useState({ ...getNow(), municipio:'', lugar:'', tipo_anterior:'' });
  const [resultado, setResultado] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [clasesServicio, setClasesServicio] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [todosLugares, setTodosLugares] = useState([]);
  const [mapCenter, setMapCenter] = useState({ lat: 14.64, lon: -89.99 }); // Jalapa default
  const [rankingSelected, setRankingSelected] = useState(''); // municipio activo en el mapa (independiente del form)
  const [busqueda, setBusqueda]   = useState('');
  const [cargando,  setCargando]  = useState(false);
  const [error,     setError]     = useState('');
  const [animating, setAnimating] = useState(false);
  const [liveTime,  setLiveTime]  = useState(new Date());
  const [claseSeleccionada, setClaseSeleccionada] = useState('');
  const [loadingClases, setLoadingClases] = useState(true);
  const [errorClases,   setErrorClases]   = useState('');
  const mounted = useRef(false);

  const [departamentos, setDepartamentos] = useState([]);
  const [departamento,  setDepartamento]  = useState('');
  const [area,          setArea]          = useState('Urbana');

  // ── Combobox lugar ────────────────────────────────────────────────────
  const [busquedaLugar,     setBusquedaLugar]     = useState('');
  const [lugarDropdownOpen, setLugarDropdownOpen] = useState(false);
  const [dropdownPos,       setDropdownPos]       = useState({});
  const lugarRef      = useRef(null);
  const lugarInputRef = useRef(null);

  const [lugarCoords,         setLugarCoords]         = useState(null);
  const [geocodificandoLugar, setGeocodificandoLugar] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setLiveTime(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const COD_DEPARTAMENTO_JALAPA = 1302;
    const COD_COMPANIA = 44;

    // Cargar departamentos y default a Jalapa
    getDepartamentos()
      .then(({ data }) => {
        const deptos = Array.isArray(data) ? data : [];
        setDepartamentos(deptos);
        const defaultDepto = deptos.find(d => d.Cod_Depto === String(COD_DEPARTAMENTO_JALAPA)) ?? deptos[0];
        if (defaultDepto) setDepartamento(defaultDepto.Cod_Depto);
      })
      .catch(() => {});

    // Cargar lugares para dropdown
    getLugares()
      .then(({ data }) => {
        setTodosLugares(Array.isArray(data) ? data : []);
      })
      .catch(() => {});

    // Cargar historial de municipios (ya contiene ubicaciones)
    api.get('/prediccion/historial-municipios')
      .then(res => {
        setHistorial(Array.isArray(res.data) ? res.data : []);
      })
      .catch(err => console.warn('Error al cargar historial municipios:', err.message));

    // Cargar incidentes para tipos de servicio
    azureApi.post('/api/BusquedaAvanzada', { Cod_Compania: COD_COMPANIA })
      .then(r => {
        if (!Array.isArray(r.data)) { setLoadingClases(false); return; }
        const clases = [...new Set(r.data.map(i => i.ClaseServicio).filter(Boolean))].sort();
        setClasesServicio(clases);
        setLoadingClases(false);
      })
      .catch(() => {
        setErrorClases('No se pudo cargar los tipos desde Azure. Puedes continuar sin seleccionar tipo.');
        setLoadingClases(false);
      });
  }, []);

  // Recarga municipios cuando cambia el departamento
  useEffect(() => {
    if (!departamento) return;
    getMunicipios(departamento)
      .then(({ data }) => {
        const lista = Array.isArray(data) ? data : [];
        setMunicipios(lista);
        setForm(p => ({ ...p, municipio: lista.length > 0 ? lista[0].Cod_Muni : '', lugar: '' }));
      })
      .catch(() => { setMunicipios([]); setForm(p => ({ ...p, municipio: '', lugar: '' })); });
  }, [departamento]);

  // Convierte Cod_Muni de Azure (numérico) al nombre que entiende la IA ("JALAPA", etc.)
  const getMunicipioIA = (codOrName) => {
    if (!codOrName) return null;
    const muniObj = municipios.find(m => String(m.Cod_Muni) === String(codOrName));
    if (muniObj?.Nombre_Muni) return muniObj.Nombre_Muni.toUpperCase().replace(/\s+/g, ' ');
    return String(codOrName).toUpperCase().replace(/\s+/g, ' ');
  };

  // Geocodificar cuando cambia el municipio del form
  useEffect(() => {
    if (!form.municipio) return;
    const muniObj = municipios.find(m => String(m.Cod_Muni) === String(form.municipio));
    const nombreNormalizado = muniObj?.Nombre_Muni
      ? muniObj.Nombre_Muni.toUpperCase().replace(/\s+/g, ' ')
      : String(form.municipio).toUpperCase().replace(/\s+/g, ' ');
    const coords = MUNICIPIO_COORDS_BY_NAME[nombreNormalizado];
    if (coords) setMapCenter(coords);
  }, [form.municipio, municipios]);

  // Mover mapa cuando se selecciona desde el ranking (usa el nombre IA directamente)
  useEffect(() => {
    if (!rankingSelected) {
      setMapCenter({ lat: 14.64, lon: -89.99 });
      return;
    }
    const coords = MUNICIPIO_COORDS_BY_NAME[rankingSelected];
    if (coords) setMapCenter(coords);
  }, [rankingSelected]);

  // Sincronizar municipio del form → rankingSelected (para highlight en heatmap)
  useEffect(() => {
    if (!form.municipio || municipios.length === 0) return;
    const muniObj = municipios.find(m => String(m.Cod_Muni) === String(form.municipio));
    if (muniObj?.Nombre_Muni) {
      const nombre = muniObj.Nombre_Muni.toUpperCase().replace(/\s+/g, ' ');
      setRankingSelected(nombre);
    }
  }, [form.municipio, municipios]);

  // Geocodificar aldea/lugar seleccionado → mover mapa + pin
  useEffect(() => {
    if (!form.lugar) { setLugarCoords(null); return; }
    const lugarObj = todosLugares.find(l => String(l.Cod_Lugar) === String(form.lugar));
    if (!lugarObj) return;
    setGeocodificandoLugar(true);
    const query = `${lugarObj.Descripcion}, ${lugarObj.Nombre_Muni || ''}, ${lugarObj.Nombre_Depto || ''}, Guatemala`;
    fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=1&countrycodes=gt`,
      { headers: { 'User-Agent': 'PIAPO/1.0' } })
      .then(r => r.json())
      .then(data => {
        if (data.length > 0) {
          const lat = parseFloat(data[0].lat);
          const lon = parseFloat(data[0].lon);
          setLugarCoords({ lat, lon, nombre: `${lugarObj.TipoLugar} ${lugarObj.Descripcion}` });
          setMapCenter({ lat, lon });
        }
      })
      .catch(() => {})
      .finally(() => setGeocodificandoLugar(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.lugar, todosLugares]);

  // Auto-analizar cuando cambia municipio o lugar (no en el primer render)
  useEffect(() => {
    if (!mounted.current) { mounted.current = true; return; }
    if (form.municipio && form.lugar) analizar(form);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.municipio, form.lugar]);

  // Limpia búsqueda cuando se deselecciona lugar
  useEffect(() => {
    if (!form.lugar) { setBusquedaLugar(''); setLugarDropdownOpen(false); }
  }, [form.lugar]);

  // Cierra dropdown al hacer clic fuera
  useEffect(() => {
    const handler = e => {
      if (lugarRef.current && !lugarRef.current.contains(e.target))
        setLugarDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Reposiciona el dropdown al hacer scroll
  useEffect(() => {
    if (!lugarDropdownOpen) return;
    const update = () => {
      if (lugarInputRef.current) {
        const r = lugarInputRef.current.getBoundingClientRect();
        setDropdownPos({ top: r.bottom + 4, left: r.left, width: r.width });
      }
    };
    window.addEventListener('scroll', update, true);
    return () => window.removeEventListener('scroll', update, true);
  }, [lugarDropdownOpen]);

  const resetToNow = useCallback(() => {
    setForm(p => ({ ...p, ...getNow() }));
  }, []);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(p => ({ ...p, [name]: name === 'municipio' || name === 'lugar' || name === 'tipo_anterior' ? value : Number(value) }));
  };

  const abrirDropdownLugar = () => {
    if (lugarInputRef.current) {
      const r = lugarInputRef.current.getBoundingClientRect();
      setDropdownPos({ top: r.bottom + 4, left: r.left, width: r.width });
    }
    setLugarDropdownOpen(true);
  };

  const seleccionarClase = (clase) => {
    if (claseSeleccionada === clase) {
      setClaseSeleccionada('');
      setForm(p => ({ ...p, tipo_anterior: '' }));
    } else {
      setClaseSeleccionada(clase);
      const tipo    = mapearTipoIncidente('', clase);
      const newForm = { ...form, tipo_anterior: tipo };
      setForm(newForm);
      if (newForm.municipio && newForm.lugar) analizar(newForm);
    }
  };

  // Nombre IA del municipio actualmente seleccionado (para highlight y predicción)
  const municipioIAName = getMunicipioIA(form.municipio);

  const analizar= async (currentForm) => {
    const f = currentForm || form;
    const municipioIA = getMunicipioIA(f.municipio);
    if (!municipioIA || !f.lugar) { 
      setError('Selecciona un municipio y lugar para continuar.'); 
      return; 
    }
    setError(''); setCargando(true); setResultado(null); setAnimating(false);
    try {
      // Derivar categoría de lugar para la IA desde descripción + toggle área
      const lugarObj = todosLugares.find(l => String(l.Cod_Lugar) === String(f.lugar));
      const lugarDesc = (lugarObj?.Descripcion || '').toLowerCase();
      let lugarIA;
      if (/carretera|ruta|camino|autopista|vía|via/.test(lugarDesc)) lugarIA = 'carretera';
      else if (/centro/.test(lugarDesc)) lugarIA = 'centro';
      else if (/norte/.test(lugarDesc))  lugarIA = 'norte';
      else if (/sur/.test(lugarDesc))    lugarIA = 'sur';
      else if (/este/.test(lugarDesc))   lugarIA = 'este';
      else if (/oeste/.test(lugarDesc))  lugarIA = 'oeste';
      else if (area === 'Urbana')        lugarIA = 'area_urbana';
      else                               lugarIA = 'area_rural';

      const payload = {
        hora: f.hora,
        dia_semana: f.dia_semana,
        mes: f.mes,
        municipio: municipioIA,
        lugar: lugarIA,
        tipo_anterior: f.tipo_anterior || null,
      };
      const res = await api.post('/prediccion/riesgo', payload);
      setResultado(res.data);
      setTimeout(() => setAnimating(true), 80);
    } catch (err) {
      console.error('Error en predicción:', err);
      setError('No se pudo conectar con el servicio de predicción. Verifica que el IA Service esté corriendo.');
    } finally {
      setCargando(false);
    }
  };

  const handleSubmit = e => { e.preventDefault(); analizar(); };

  const maxZona  = historial.length ? Math.max(...historial.map(h => h.total_incidentes)) : 1;
  const nowHora  = liveTime.getHours();
  const nowDia   = liveTime.getDay() === 0 ? 6 : liveTime.getDay() - 1;
  const nowMes   = liveTime.getMonth() + 1;
  const isNow    = form.hora === nowHora && form.dia_semana === nowDia && form.mes === nowMes;

  // Ordenar niveles: top primero, resto por order
  const riskEntries = resultado?.probabilidades
    ? Object.entries(resultado.probabilidades).sort(([a],[b]) => {
        if (a === resultado.riesgo) return -1;
        if (b === resultado.riesgo) return 1;
        return (NIVEL[a]?.order ?? 0) - (NIVEL[b]?.order ?? 0);
      })
    : [];

  return (
    <div style={{ minHeight:'100vh', background:'#f0f4f8', fontFamily:"'Inter','Segoe UI',sans-serif", overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
        .pia-select {
          width:100%; border:1.5px solid #e2e8f0; border-radius:10px;
          padding:11px 14px; font-size:.9rem; color:#1e293b;
          background:#fff; outline:none; cursor:pointer;
          transition:border-color .2s, box-shadow .2s; font-family:'Inter',sans-serif;
        }
        .pia-select:focus { border-color:#3b82f6; box-shadow:0 0 0 3px rgba(59,130,246,0.12); }
        .pia-select:hover { border-color:#94a3b8; }
        .pia-btn {
          width:100%; padding:14px; border:none; border-radius:12px;
          background:linear-gradient(135deg,#2563eb,#1d4ed8);
          color:#fff; font-size:.95rem; font-weight:700; cursor:pointer;
          transition:all .2s; display:flex; align-items:center; justify-content:center; gap:8px;
          box-shadow:0 4px 16px rgba(37,99,235,0.35); font-family:'Inter',sans-serif;
        }
        .pia-btn:hover:not(:disabled) { transform:translateY(-1px); box-shadow:0 6px 22px rgba(37,99,235,0.45); }
        .pia-btn:disabled { background:#93c5fd; box-shadow:none; cursor:not-allowed; }
        .pia-card { background:#fff; border-radius:18px; box-shadow:0 1px 3px rgba(0,0,0,0.05),0 8px 28px rgba(0,0,0,0.06); }
        .pia-label { display:block; font-size:.71rem; font-weight:700; color:#64748b; margin-bottom:7px; text-transform:uppercase; letter-spacing:.09em; }
        .hist-row { transition:background .15s; cursor:pointer; }
        .hist-row:hover { background:#f8fafc; }
        .now-btn { background:#f0f9ff; border:1px solid #bae6fd; border-radius:8px; padding:5px 11px; font-size:.72rem; font-weight:700; color:#0284c7; cursor:pointer; transition:all .2s; }
        .now-btn:hover { background:#e0f2fe; }
        .tipo-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:8px; }
        .tipo-card {
          display:flex; flex-direction:column; align-items:center; justify-content:center;
          gap:5px; padding:12px 6px; border-radius:12px; cursor:pointer;
          border:2px solid #e2e8f0; background:#f8fafc;
          transition:all .18s; text-align:center;
        }
        .tipo-card:hover { border-color:#94a3b8; background:#fff; transform:translateY(-1px); box-shadow:0 3px 10px rgba(0,0,0,0.08); }
        .tipo-card.selected { background:#eff6ff; border-color:#3b82f6; box-shadow:0 0 0 3px rgba(59,130,246,0.12); }
        .tipo-card-icon { font-size:1.6rem; line-height:1; }
        .tipo-card-label { font-size:.68rem; font-weight:700; color:#475569; line-height:1.2; }
        .tipo-card.selected .tipo-card-label { color:#2563eb; }
        .tipo-ninguno { display:flex; align-items:center; justify-content:center; gap:6px; padding:9px; border-radius:10px; cursor:pointer; border:1.5px dashed #cbd5e1; background:#f8fafc; color:#94a3b8; font-size:.78rem; font-weight:600; transition:all .18s; }
        .tipo-ninguno:hover { border-color:#94a3b8; color:#64748b; }
        .tipo-ninguno.selected { border-color:#64748b; background:#f1f5f9; color:#475569; }
        @keyframes fadeInUp  { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
        @keyframes pulseGlow { 0%,100%{opacity:.7} 50%{opacity:.3} }
        @keyframes spin      { to{transform:rotate(360deg)} }
        .result-enter { animation:fadeInUp .45s cubic-bezier(.2,.8,.3,1) both; }

        /* ── RESPONSIVE ── */
        @media (max-width: 1200px) {
          .pia-hero-title { font-size: 1.8rem !important; }
          .pia-hero-icon { width: 50px !important; height: 50px !important; font-size: 24px !important; }
          .pia-hero-stats { gap: 8px !important; }
          .pia-hero-stat-box { padding: 10px 12px !important; }
          .pia-hero-stat-val { font-size: .75rem !important; }
          .pia-hero { padding: 32px 24px 28px !important; overflow-x: hidden !important; }
        }

        @media (max-width: 1024px) {
          .pia-select { font-size: .85rem; padding: 10px 12px; }
          .pia-btn { padding: 12px; font-size: .9rem; }
          .pia-card { border-radius: 14px; }
          .pia-label { font-size: .65rem; margin-bottom: 6px; }
          .tipo-grid { grid-template-columns: repeat(3, 1fr); gap: 7px; }
          .tipo-card { padding: 10px 5px; gap: 4px; }
          .tipo-card-icon { font-size: 1.4rem; }
          .tipo-card-label { font-size: .62rem; }
          .hist-row { padding: 10px 14px; }
          .pia-hero { padding: 32px 24px 28px !important; overflow-x: hidden !important; }
          .pia-main-grid { grid-template-columns: 1fr !important; }
          .pia-clase-grid { grid-template-columns: repeat(auto-fill, minmax(120px, 1fr)) !important; gap: 8px !important; }
          .pia-clase-header { flex-direction: row !important; }
          .pia-clase-search { width: 180px !important; }
        }

        @media (max-width: 768px) {
          .pia-select { font-size: .8rem; padding: 8px 10px; }
          .pia-btn { padding: 10px; font-size: .8rem; }
          .pia-card { border-radius: 12px; }
          .pia-label { font-size: .6rem; margin-bottom: 5px; }
          .tipo-grid { grid-template-columns: repeat(3, 1fr); gap: 6px; }
          .tipo-card { padding: 8px 4px; gap: 3px; border-radius: 10px; }
          .tipo-card-icon { font-size: 1.2rem; }
          .tipo-card-label { font-size: .58rem; }
          .tipo-ninguno { padding: 7px; font-size: .7rem; }
          .hist-row { padding: 8px 12px; font-size: .8rem; }
          .hist-row-rank { font-size: .7rem; }
          .pia-hero { padding: 20px 16px 16px !important; overflow-x: hidden !important; }
          .pia-hero-title { font-size: 1.4rem !important; }
          .pia-hero-sub { font-size: .75rem !important; }
          .pia-hero-icon { width: 44px !important; height: 44px !important; font-size: 20px !important; }
          .pia-hero-gap { gap: 12px !important; }
          .pia-hero-stats { gap: 6px !important; flex-wrap: wrap; }
          .pia-hero-stat-box { padding: 8px 10px !important; border-radius: 10px !important; }
          .pia-hero-stat-val { font-size: .65rem !important; }
          .pia-hero-stat-lbl { font-size: .55rem !important; }
          .pia-main { padding: 16px 12px !important; overflow-x: hidden !important; overflow-y: auto; width: 100% !important; }
          .pia-main-grid { grid-template-columns: 1fr !important; gap: 14px !important; width: 100% !important; max-width: 100% !important; }
          .pia-risk-grid { gap: 8px !important; }
          .pia-params { gap: 5px !important; padding: 8px 12px !important; }
          .pia-params-tag { font-size: .65rem !important; padding: 3px 8px !important; }
          .pia-map-section { grid-template-columns: 1fr !important; }
          .pia-map-card { height: 300px !important; }
          .pia-ranking-card { max-height: 280px !important; }
          .pia-ranking-header { padding: 10px 14px !important; }
          .pia-ranking-item { padding: 10px 12px !important; }
          .now-btn { padding: 4px 9px; font-size: .65rem; }
          .pia-error { font-size: .75rem; padding: 8px 10px; }
          .pia-clase-grid { grid-template-columns: repeat(auto-fill, minmax(100px, 1fr)) !important; gap: 8px !important; }
          .pia-clase-card { padding: 14px 10px 12px !important; font-size: .7rem !important; }
          .pia-clase-icon { font-size: 1.9rem !important; margin-bottom: 8px !important; }
          .pia-clase-header { flex-direction: column !important; align-items: flex-start !important; gap: 10px !important; padding: 14px 16px !important; }
          .pia-clase-search { width: 100% !important; }
        }

        @media (max-width: 480px) {
          .pia-select { font-size: .75rem; padding: 7px 9px; border-radius: 8px; }
          .pia-btn { padding: 8px 10px; font-size: .7rem; border-radius: 10px; }
          .pia-card { border-radius: 10px; }
          .pia-label { font-size: .55rem; margin-bottom: 4px; letter-spacing: .05em; }
          .tipo-grid { grid-template-columns: repeat(3, 1fr); gap: 5px; }
          .tipo-card { padding: 6px 3px; gap: 2px; border-radius: 8px; border: 1.5px solid #e2e8f0; }
          .tipo-card-icon { font-size: 1rem; }
          .tipo-card-label { font-size: .52rem; line-height: 1; }
          .tipo-ninguno { padding: 6px; font-size: .65rem; border-radius: 8px; }
          .hist-row { padding: 6px 10px; font-size: .7rem; }
          .hist-row-rank { font-size: .65rem; }
          .pia-hero { padding: 12px 10px 10px !important; overflow-x: hidden !important; }
          .pia-hero-top { flex-direction: column !important; align-items: flex-start !important; gap: 10px !important; }
          .pia-hero-left { gap: 10px !important; }
          .pia-hero-title { font-size: 1.15rem !important; }
          .pia-hero-sub { font-size: .65rem !important; }
          .pia-hero-icon { width: 36px !important; height: 36px !important; font-size: 16px !important; border-radius: 12px !important; }
          .pia-hero-label { font-size: .6rem !important; }
          .pia-hero-stats { gap: 4px !important; width: 100%; }
          .pia-hero-stat-box { padding: 6px 8px !important; border-radius: 8px !important; flex: 1; }
          .pia-hero-stat-icon { font-size: .8rem !important; margin-bottom: 1px !important; }
          .pia-hero-stat-val { font-size: .55rem !important; }
          .pia-hero-stat-lbl { font-size: .45rem !important; }
          .pia-main { padding: 10px 8px !important; overflow-x: hidden !important; }
          .pia-main-grid { grid-template-columns: 1fr !important; gap: 10px !important; }
          .pia-form-grid { grid-template-columns: 1fr !important; gap: 8px !important; }
          .pia-form-col { grid-column: unset !important; }
          .pia-risk-grid { gap: 6px !important; }
          .pia-risk-top { margin-bottom: 8px !important; }
          .pia-risk-bottom { grid-template-columns: repeat(2, 1fr) !important; }
          .pia-params { gap: 4px !important; padding: 6px 10px !important; }
          .pia-params-tag { font-size: .6rem !important; padding: 2px 6px !important; }
          .pia-map-header { flex-direction: column !important; align-items: flex-start !important; }
          .pia-map-title { font-size: .85rem !important; }
          .pia-map-legend { flex-wrap: wrap; gap: 4px !important; }
          .pia-legend-item { font-size: .6rem !important; }
          .pia-map-section { grid-template-columns: 1fr !important; gap: 10px !important; }
          .pia-map-card { height: 250px !important; border-radius: 10px !important; }
          .pia-ranking-card { overflow-y: auto; max-height: 250px !important; border-radius: 10px !important; }
          .pia-ranking-header { padding: 8px 10px !important; font-size: .65rem !important; }
          .pia-ranking-item { padding: 8px 10px !important; }
          .pia-ranking-medal { font-size: .9rem; width: 22px; }
          .pia-ranking-zone { font-size: .75rem; }
          .pia-ranking-cases { font-size: .65rem; }
          .pia-ranking-bar { height: 5px; border-radius: 99px; }
          .pia-ranking-type { font-size: .6rem; }
          .now-btn { padding: 3px 7px; font-size: .6rem; }
          .pia-placeholder { padding: 40px 20px !important; }
          .pia-placeholder-icon { font-size: 48px !important; margin-bottom: 10px !important; }
          .pia-placeholder-text { font-size: .85rem !important; }
          .pia-error { font-size: .65rem; padding: 6px 8px; }
          .pia-clase-grid { grid-template-columns: repeat(auto-fill, minmax(75px, 1fr)) !important; gap: 6px !important; }
          .pia-clase-card { padding: 12px 7px 10px !important; font-size: .62rem !important; border-radius: 10px !important; }
          .pia-clase-icon { font-size: 1.6rem !important; margin-bottom: 5px !important; }
          .pia-clase-header { flex-direction: column !important; align-items: stretch !important; gap: 8px !important; padding: 12px 14px !important; }
          .pia-clase-header-left { flex-direction: column !important; gap: 6px !important; }
          .pia-clase-header-right { flex-direction: column !important; gap: 8px !important; width: 100% !important; }
          .pia-clase-search { width: 100% !important; }
          .pia-clase-info { font-size: .65rem !important; }
        }

        /* ── LUGAR COMBOBOX ─────────────────────────────────────────────── */
        .pia-lugar-combobox { position: relative; }
        .pia-lugar-input-row { position: relative; display: flex; align-items: center; }
        .pia-lugar-search-ico {
          position: absolute; left: 12px; top: 50%; transform: translateY(-50%);
          color: #94a3b8; pointer-events: none;
        }
        .pia-lugar-search-ico svg { width: 15px; height: 15px; }
        .pia-lugar-input {
          width:100%; border:1.5px solid #e2e8f0; border-radius:10px;
          padding:11px 36px; font-size:.9rem; color:#1e293b;
          background:#fff; outline:none; cursor:text;
          transition:border-color .2s, box-shadow .2s; font-family:'Inter',sans-serif;
          box-sizing: border-box;
        }
        .pia-lugar-input:focus { border-color:#3b82f6; box-shadow:0 0 0 3px rgba(59,130,246,0.12); }
        .pia-lugar-clear {
          position: absolute; right: 10px; top: 50%; transform: translateY(-50%);
          background: #e2e8f0; border: none; border-radius: 50%;
          width: 20px; height: 20px; cursor: pointer; color: #64748b;
          display: flex; align-items: center; justify-content: center;
          font-size: .85rem; transition: background .15s;
        }
        .pia-lugar-clear:hover { background: #cbd5e1; }
        .pia-lugar-dropdown {
          position: fixed; z-index: 9999;
          background: #fff; border: 1.5px solid #e2e8f0; border-radius: 12px;
          box-shadow: 0 8px 24px rgba(0,0,0,.1);
          max-height: 210px; overflow-y: auto;
        }
        .pia-lugar-option {
          padding: 10px 14px; font-size: .88rem; color: #1e293b; cursor: pointer;
          border-bottom: 1px solid #f1f5f9; transition: background .12s, color .12s;
        }
        .pia-lugar-option:last-child { border-bottom: none; }
        .pia-lugar-option:hover { background: #eff6ff; color: #2563eb; }
        .pia-lugar-option.selected { background: #eff6ff; color: #2563eb; font-weight: 600; }
        .pia-lugar-option-empty { padding: 14px; text-align: center; color: #94a3b8; font-size: .82rem; }

        /* ── ÁREA TOGGLE ────────────────────────────────────────────────── */
        .pia-area-toggle { display: flex; gap: 8px; }
        .pia-area-btn {
          flex: 1; padding: 11px 8px; border-radius: 10px;
          border: 1.5px solid #e2e8f0; background: #f8fafc;
          font-size: .88rem; font-weight: 600; color: #94a3b8;
          cursor: pointer; transition: all .2s; font-family: 'Inter', sans-serif;
        }
        .pia-area-btn:hover { border-color: #cbd5e1; background: #fff; color: #475569; }
        .pia-area-btn.active {
          border-color: #3b82f6; background: #eff6ff; color: #2563eb;
          box-shadow: 0 0 0 3px rgba(59,130,246,.1);
        }
        .pia-lugar-tooltip {
          background: #0f172a !important;
          color: #fff !important;
          border: none !important;
          border-radius: 8px !important;
          padding: 5px 10px !important;
          font-size: .76rem !important;
          font-weight: 700 !important;
          white-space: nowrap !important;
          box-shadow: 0 4px 14px rgba(0,0,0,.35) !important;
        }
        .pia-lugar-tooltip::before { display: none !important; }
      `}</style>

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <div className="pia-hero" style={{
        background:'linear-gradient(135deg,#0f172a 0%,#1e293b 55%,#450a0a 100%)',
        padding:'42px 36px 38px', position:'relative', overflow:'hidden',
      }}>
        <div style={{ position:'absolute',top:'-70px',right:'-70px',width:'280px',height:'280px',borderRadius:'50%',background:'rgba(239,68,68,0.07)' }} />
        <div style={{ position:'absolute',inset:0,backgroundImage:'radial-gradient(circle at 20% 50%, rgba(37,99,235,0.06) 0%, transparent 50%)' }} />
        <div style={{ position:'relative',zIndex:1,maxWidth:'1200px',margin:'0 auto' }}>
          <div className="pia-hero-top" style={{ display:'flex',alignItems:'flex-start',justifyContent:'space-between',flexWrap:'wrap',gap:'24px' }}>
            <div className="pia-hero-left" style={{ display:'flex',alignItems:'center',gap:'18px' }}>
              <div className="pia-hero-icon" style={{
                width:'60px',height:'60px',borderRadius:'18px',
                background:'linear-gradient(135deg,#ef4444,#b91c1c)',
                display:'flex',alignItems:'center',justifyContent:'center',
                fontSize:'30px',boxShadow:'0 8px 24px rgba(220,38,38,0.55)',flexShrink:0,
              }}>🤖</div>
              <div>
                <p className="pia-hero-label" style={{ margin:0,color:'rgba(255,255,255,0.45)',fontSize:'.7rem',fontWeight:700,letterSpacing:'.14em',textTransform:'uppercase',marginBottom:'5px' }}>
                  Motor de Análisis Predictivo — PIAPO
                </p>
                <h1 className="pia-hero-title" style={{ margin:0,color:'#fff',fontSize:'2.1rem',fontWeight:900,letterSpacing:'-.03em',lineHeight:1 }}>
                  Predicción IA
                </h1>
                <p className="pia-hero-sub" style={{ margin:'7px 0 0',color:'rgba(255,255,255,0.4)',fontSize:'.82rem' }}>
                  Cuerpo Voluntario de Bomberos · Jalapa, Guatemala
                </p>
              </div>
            </div>
            <div className="pia-hero-stats" style={{ display:'flex',gap:'10px',flexWrap:'wrap',alignItems:'center' }}>
              {[
                { icon:'🧠', val:'Random Forest', lbl:'Algoritmo' },
                { icon:'📊', val:'4 Niveles',     lbl:'Clasificación' },
                { icon:'🗃️', val:'3,000',         lbl:'Registros' },
                { icon:'⚡', val:'Tiempo Real',   lbl:'Análisis' },
              ].map(s => (
                <div className="pia-hero-stat-box" key={s.lbl} style={{
                  background:'rgba(255,255,255,0.07)',backdropFilter:'blur(12px)',
                  border:'1px solid rgba(255,255,255,0.09)',borderRadius:'14px',
                  padding:'12px 16px',textAlign:'center',
                }}>
                  <div className="pia-hero-stat-icon" style={{ fontSize:'1.15rem',marginBottom:'3px' }}>{s.icon}</div>
                  <div className="pia-hero-stat-val" style={{ color:'#fff',fontSize:'.83rem',fontWeight:700,whiteSpace:'nowrap' }}>{s.val}</div>
                  <div className="pia-hero-stat-lbl" style={{ color:'rgba(255,255,255,0.38)',fontSize:'.67rem',textTransform:'uppercase',letterSpacing:'.07em' }}>{s.lbl}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── BODY ─────────────────────────────────────────────────────────── */}
      <div className="pia-main" style={{ maxWidth:'1400px',margin:'0 auto',padding:'28px 24px',overflow:'hidden',width:'100%' }}>

        {/* ── TIPO DE INCIDENTE — SECCIÓN FULL-WIDTH ─────────────────────── */}
        <div className="pia-card" style={{ overflow:'hidden',marginBottom:'24px' }}>

          {/* Header */}
          <div className="pia-clase-header" style={{ padding:'18px 26px',borderBottom:'1px solid #f1f5f9',display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:'14px',overflow:'hidden' }}>
            <div className="pia-clase-header-left" style={{ display:'flex',alignItems:'center',gap:'14px',minWidth:0,flex:1 }}>
              <div style={{ width:'40px',height:'40px',borderRadius:'11px',background:'linear-gradient(135deg,#ef4444,#b91c1c)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'19px',flexShrink:0 }}>📋</div>
              <div style={{ minWidth:0 }}>
                <h2 style={{ margin:0,fontSize:'1rem',fontWeight:800,color:'#0f172a' }}>Clase de Servicio / Tipo de Incidente</h2>
                <p style={{ margin:0,fontSize:'.73rem',color:'#94a3b8' }}>Seleccioná el tipo para predecir su nivel de riesgo — opcional</p>
              </div>
            </div>
            <div className="pia-clase-header-right" style={{ display:'flex',alignItems:'center',gap:'10px',flexWrap:'wrap',justifyContent:'flex-end' }}>
              {claseSeleccionada && (
                <div style={{ display:'flex',alignItems:'center',gap:'6px',background:'#f0fdf4',border:'1.5px solid #bbf7d0',borderRadius:'99px',padding:'5px 12px',flexShrink:0 }}>
                  <span style={{ width:'8px',height:'8px',borderRadius:'50%',background:'#10b981',display:'inline-block',flexShrink:0 }} />
                  <span style={{ fontSize:'.75rem',fontWeight:700,color:'#065f46',maxWidth:'180px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{claseSeleccionada}</span>
                  <button type="button" onClick={() => { setClaseSeleccionada(''); setForm(p=>({...p,tipo_anterior:''})); }} style={{ background:'none',border:'none',color:'#6ee7b7',cursor:'pointer',padding:'0 0 0 4px',fontSize:'.9rem',lineHeight:1,flexShrink:0 }}>✕</button>
                </div>
              )}
              <div style={{ position:'relative',flexShrink:0 }}>
                <span style={{ position:'absolute',left:'10px',top:'50%',transform:'translateY(-50%)',color:'#94a3b8',fontSize:'.85rem',pointerEvents:'none' }}>🔍</span>
                <input
                  className="pia-clase-search"
                  type="text"
                  placeholder="Buscar tipo de incidente..."
                  value={busqueda}
                  onChange={e => setBusqueda(e.target.value)}
                  style={{ border:'1.5px solid #e2e8f0',borderRadius:'10px',padding:'7px 14px 7px 32px',fontSize:'.82rem',outline:'none',fontFamily:'Inter,sans-serif',width:'220px',minWidth:'120px' }}
                />
              </div>
              <span className="pia-clase-info" style={{ fontSize:'.7rem',color:'#94a3b8',fontWeight:600,background:'#f8fafc',border:'1px solid #e2e8f0',borderRadius:'99px',padding:'4px 10px',whiteSpace:'nowrap',flexShrink:0 }}>
                {clasesServicio.length} tipos
              </span>
            </div>
          </div>

          {/* Grid */}
          <div style={{ padding:'22px 26px' }}>
            {loadingClases ? (
              <div style={{ textAlign:'center',padding:'36px',color:'#94a3b8',fontSize:'.85rem',display:'flex',alignItems:'center',justifyContent:'center',gap:'10px' }}>
                <span style={{ display:'inline-block',width:'14px',height:'14px',border:'2px solid #cbd5e1',borderTopColor:'#94a3b8',borderRadius:'50%',animation:'spin .8s linear infinite' }} />
                Cargando tipos de incidentes desde Azure...
              </div>
            ) : errorClases ? (
              <div style={{ textAlign:'center',padding:'28px',color:'#b45309',fontSize:'.83rem',background:'#fffbeb',border:'1px solid #fde68a',borderRadius:'12px',display:'flex',alignItems:'center',justifyContent:'center',gap:'10px' }}>
                <span>⚠️</span> {errorClases}
              </div>
            ) : clasesServicio.length === 0 ? (
              <div style={{ textAlign:'center',padding:'28px',color:'#94a3b8',fontSize:'.83rem' }}>
                Sin tipos disponibles
              </div>
            ) : (
              <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))',gap:'10px' }} className="pia-clase-grid">
                {clasesServicio
                  .filter(c => !busqueda || c.toLowerCase().includes(busqueda.toLowerCase()))
                  .map(clase => {
                    const tipo       = mapearTipoIncidente('', clase);
                    const col        = TIPO_COLOR[tipo] || '#64748b';
                    const icon       = CLASE_ICONS[clase] || TIPOS_INCIDENTE.find(t=>t.value===tipo)?.icon || '📋';
                    const isSelected = claseSeleccionada === clase;
                    return (
                      <div
                        key={clase}
                        onClick={() => seleccionarClase(clase)}
                        className="pia-clase-card"
                        style={{
                          background:   isSelected ? `${col}0e` : '#fff',
                          border:       `2px solid ${isSelected ? col : '#e8edf2'}`,
                          borderRadius: '14px',
                          padding:      '18px 12px 14px',
                          cursor:       'pointer',
                          textAlign:    'center',
                          transition:   'all .16s',
                          boxShadow:    isSelected ? `0 4px 16px ${col}28` : '0 1px 4px rgba(0,0,0,0.05)',
                          position:     'relative',
                        }}
                      >
                        {isSelected && (
                          <span style={{ position:'absolute',top:'7px',right:'8px',background:col,color:'#fff',fontSize:'.52rem',fontWeight:800,borderRadius:'99px',padding:'2px 6px',letterSpacing:'.04em' }}>✓</span>
                        )}
                        <div className="pia-clase-icon" style={{ fontSize:'2.1rem',marginBottom:'9px',lineHeight:1 }}>{icon}</div>
                        <div style={{ fontSize:'.73rem',fontWeight:700,color:isSelected ? col : '#374151',lineHeight:1.4,wordBreak:'break-word' }}>
                          {clase}
                        </div>
                      </div>
                    );
                  })
                }
              </div>
            )}
          </div>
        </div>

        {/* ── FORM + RESULTS GRID ──────────────────────────────────────────── */}
        <div className="pia-main-grid" style={{ display:'grid',gridTemplateColumns:'360px 1fr',gap:'24px',alignItems:'start',maxWidth:'100%',overflow:'hidden' }}>

          {/* ── FORM ──────────────────────────────────────────────────────── */}
          <div className="pia-card" style={{ overflow:'hidden',minWidth:0 }}>
            <div style={{ height:'3px',background:'linear-gradient(90deg,#2563eb,#7c3aed,#ef4444)',borderRadius:'18px 18px 0 0' }} />
            <div style={{ padding:'20px 24px',borderBottom:'1px solid #f1f5f9',display:'flex',alignItems:'center',gap:'12px' }}>
              <div style={{ width:'36px',height:'36px',borderRadius:'10px',background:'linear-gradient(135deg,#eff6ff,#dbeafe)',border:'1px solid #bfdbfe',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'17px',flexShrink:0 }}>⚙️</div>
              <div>
                <h2 style={{ margin:0,fontSize:'.95rem',fontWeight:700,color:'#1e293b' }}>Parámetros del Análisis</h2>
                <p style={{ margin:0,fontSize:'.73rem',color:'#94a3b8' }}>Configura zona y tiempo para predecir el riesgo</p>
              </div>
            </div>

            <form className="pia-form-grid" onSubmit={handleSubmit} style={{ padding:'24px',display:'flex',flexDirection:'column',gap:'18px' }}>

              {/* Tipo seleccionado (badge si viene del grid de arriba) */}
              {claseSeleccionada && (() => {
                const tipoSel = mapearTipoIncidente('', claseSeleccionada);
                const colSel  = TIPO_COLOR[tipoSel] || '#3b82f6';
                const iconSel = CLASE_ICONS[claseSeleccionada] || TIPOS_INCIDENTE.find(t=>t.value===tipoSel)?.icon || '📋';
                return (
                  <div style={{ display:'flex',alignItems:'center',gap:'10px',background:`linear-gradient(135deg,${colSel}10,${colSel}05)`,border:`1.5px solid ${colSel}30`,borderLeft:`4px solid ${colSel}`,borderRadius:'12px',padding:'12px 14px' }}>
                    <span style={{ fontSize:'1.5rem',lineHeight:1 }}>{iconSel}</span>
                    <div style={{ flex:1,minWidth:0 }}>
                      <div style={{ fontSize:'.65rem',color:colSel,textTransform:'uppercase',letterSpacing:'.08em',fontWeight:800,marginBottom:'2px' }}>Incidencia seleccionada</div>
                      <div style={{ fontSize:'.85rem',fontWeight:700,color:'#1e293b',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{claseSeleccionada}</div>
                    </div>
                    <span style={{ background:colSel,color:'#fff',fontSize:'.58rem',fontWeight:800,borderRadius:'99px',padding:'3px 9px',flexShrink:0 }}>ACTIVO</span>
                  </div>
                );
              })()}

              {/* ── DEPARTAMENTO ──────────────────────────────────── */}
              <div>
                <label className="pia-label">🗺️ Departamento</label>
                <select
                  value={departamento}
                  onChange={e => {
                    setDepartamento(e.target.value);
                    setArea('Urbana');
                    setForm(p => ({ ...p, municipio: '', lugar: '' }));
                  }}
                  className="pia-select"
                >
                  <option value="">Seleccionar departamento</option>
                  {departamentos.map(d => (
                    <option key={d.Cod_Depto} value={d.Cod_Depto}>{d.Nombre_Depto}</option>
                  ))}
                </select>
              </div>

              {/* ── MUNICIPIO + ÁREA ──────────────────────────────── */}
              <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:'12px' }}>
                <div>
                  <label className="pia-label">🏘️ Municipio</label>
                  <select
                    name="municipio"
                    value={form.municipio || ''}
                    onChange={e => setForm(p => ({ ...p, municipio: e.target.value, lugar: '' }))}
                    className="pia-select"
                  >
                    <option value="">Seleccionar municipio</option>
                    {municipios.map(m => (
                      <option key={m.Cod_Muni} value={m.Cod_Muni}>{m.Nombre_Muni}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="pia-label">🌐 Área</label>
                  <div className="pia-area-toggle">
                    {['Urbana', 'Rural'].map(op => (
                      <button
                        key={op}
                        type="button"
                        className={`pia-area-btn${area === op ? ' active' : ''}`}
                        onClick={() => { setArea(op); setForm(p => ({ ...p, lugar: '' })); }}
                      >
                        {op === 'Urbana' ? '🏙️' : '🌄'} {op}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* ── LUGAR (combobox buscador) ──────────────────────── */}
              {(() => {
                const lugaresDisponibles = todosLugares.filter(l =>
                  (!form.municipio || String(l.Cod_Muni) === String(form.municipio)) &&
                  l.Area && l.Area.trim() === area
                );
                return (
                  <div>
                    <label className="pia-label">
                      📍 {area === 'Urbana' ? 'Colonia / Barrio' : 'Aldea / Caserío'}
                      {geocodificandoLugar && (
                        <span style={{ marginLeft:8, fontSize:'.68rem', color:'#94a3b8', fontWeight:400 }}>
                          🔄 Localizando...
                        </span>
                      )}
                      {lugarCoords && !geocodificandoLugar && (
                        <span style={{ marginLeft:8, fontSize:'.68rem', color:'#22c55e', fontWeight:600 }}>
                          ✓ En mapa
                        </span>
                      )}
                    </label>
                    <div className="pia-lugar-combobox" ref={lugarRef}>
                      <div className="pia-lugar-input-row">
                        <span className="pia-lugar-search-ico">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
                          </svg>
                        </span>
                        <input
                          ref={lugarInputRef}
                          type="text"
                          className="pia-lugar-input"
                          placeholder={`Buscar ${area === 'Urbana' ? 'colonia o barrio' : 'aldea o caserío'}...`}
                          value={busquedaLugar}
                          onFocus={abrirDropdownLugar}
                          onChange={e => {
                            setBusquedaLugar(e.target.value);
                            setForm(p => ({ ...p, lugar: '' }));
                            abrirDropdownLugar();
                          }}
                        />
                        {busquedaLugar && (
                          <button
                            type="button"
                            className="pia-lugar-clear"
                            onClick={() => { setBusquedaLugar(''); setForm(p => ({ ...p, lugar: '' })); }}
                            title="Limpiar"
                          >×</button>
                        )}
                      </div>
                      {lugarDropdownOpen && (
                        <div className="pia-lugar-dropdown" style={dropdownPos}>
                          {(() => {
                            const filtrados = lugaresDisponibles.filter(l =>
                              busquedaLugar.trim() === '' ||
                              `${l.TipoLugar} ${l.Descripcion}`.toLowerCase().includes(busquedaLugar.toLowerCase())
                            );
                            return filtrados.length === 0 ? (
                              <div className="pia-lugar-option-empty">
                                {lugaresDisponibles.length === 0
                                  ? 'Selecciona un municipio primero'
                                  : `Sin resultados para "${busquedaLugar}"`}
                              </div>
                            ) : filtrados.map(l => (
                              <div
                                key={l.Cod_Lugar}
                                className={`pia-lugar-option${String(form.lugar) === String(l.Cod_Lugar) ? ' selected' : ''}`}
                                onMouseDown={e => {
                                  e.preventDefault();
                                  setForm(p => ({ ...p, lugar: String(l.Cod_Lugar) }));
                                  setBusquedaLugar(`${l.TipoLugar} ${l.Descripcion}`);
                                  setLugarDropdownOpen(false);
                                }}
                              >
                                {l.TipoLugar} {l.Descripcion}
                              </div>
                            ));
                          })()}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Hora + Día */}
              <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:'14px' }}>
                <div>
                  <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'7px' }}>
                    <label className="pia-label" style={{ margin:0 }}>🕐 Hora</label>
                    {isNow && <span style={{ background:'#dcfce7',color:'#16a34a',fontSize:'.62rem',fontWeight:800,borderRadius:'99px',padding:'2px 8px' }}>AHORA</span>}
                  </div>
                  <select name="hora" value={form.hora} onChange={handleChange} className="pia-select">
                    {Array.from({length:24},(_,i) => (
                      <option key={i} value={i}>{String(i).padStart(2,'0')}:00{i===nowHora ? ' ← ahora' : ''}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'7px' }}>
                    <label className="pia-label" style={{ margin:0 }}>📅 Día</label>
                  </div>
                  <select name="dia_semana" value={form.dia_semana} onChange={handleChange} className="pia-select">
                    {DIAS.map((d,i) => <option key={i} value={i}>{d}{i===nowDia ? ' ←' : ''}</option>)}
                  </select>
                </div>
              </div>

              {/* Mes */}
              <div>
                <label className="pia-label">🗓️ Mes</label>
                <select name="mes" value={form.mes} onChange={handleChange} className="pia-select">
                  {MESES.map((m,i) => <option key={i+1} value={i+1}>{m}{(i+1)===nowMes ? ' ← actual' : ''}</option>)}
                </select>
              </div>

              {/* Botón volver a ahora */}
              {!isNow && (
                <button type="button" onClick={resetToNow} className="now-btn" style={{ alignSelf:'flex-start' }}>
                  🔄 Volver a tiempo actual
                </button>
              )}

              {error && (
                <div className="pia-error" style={{ background:'#fef2f2',border:'1px solid #fecaca',borderRadius:'10px',padding:'10px 14px',color:'#b91c1c',fontSize:'.85rem',display:'flex',gap:'8px' }}>
                  <span>⚠️</span>{error}
                </div>
              )}

              <button type="submit" disabled={cargando} className="pia-btn">
                {cargando
                  ? <><span style={{ width:'16px',height:'16px',border:'2px solid rgba(255,255,255,0.3)',borderTopColor:'#fff',borderRadius:'50%',display:'inline-block',animation:'spin .7s linear infinite' }} />Analizando...</>
                  : <>🔍 Analizar Riesgo</>
                }
              </button>
            </form>
          </div>

          {/* ── RIGHT COLUMN ──────────────────────────────────────────────── */}
          <div style={{ display:'flex',flexDirection:'column',gap:'20px' }}>

            {/* Placeholder */}
            {!resultado && !cargando && (
              <div className="pia-card" style={{ padding:'60px 40px',textAlign:'center' }}>
                <div className="pia-placeholder-icon" style={{ fontSize:'64px',marginBottom:'16px',opacity:.15,lineHeight:1 }}>🤖</div>
                <p className="pia-placeholder-text" style={{ color:'#94a3b8',fontSize:'1rem',margin:0,lineHeight:1.75,fontWeight:500 }}>
                  Selecciona un municipio y lugar, luego presiona <strong style={{ color:'#475569' }}>Analizar Riesgo</strong>
                </p>
                <p style={{ color:'#cbd5e1',fontSize:'.8rem',marginTop:'10px' }}>
                  Hora, día y mes ya están configurados al momento actual
                </p>
              </div>
            )}

            {/* Loading */}
            {cargando && (
              <div className="pia-card" style={{ padding:'60px 40px',textAlign:'center',background:'#f0f9ff' }}>
                <div style={{ fontSize:'48px',marginBottom:'16px',animation:'pulseGlow 1.4s ease infinite' }}>⚙️</div>
                <p style={{ color:'#2563eb',fontWeight:700,margin:0 }}>Procesando con Random Forest</p>
                <p style={{ color:'#93c5fd',fontSize:'.82rem',marginTop:'6px' }}>Evaluando factores temporales y geográficos...</p>
              </div>
            )}

            {/* ── 4 RISK CARDS ─────────────────────────────────────────────── */}
            {resultado && (
              <div className="result-enter">
                {/* Risk cards: top full-width + 3 compact row */}
                <div style={{ display:'flex',flexDirection:'column',gap:'12px' }}>
                  <div className="pia-risk-top">
                    {riskEntries.slice(0,1).map(([nivel, prob]) => (
                      <RiskCard key={nivel} nivel={nivel} pct={Math.round(prob * 100)} isTop={true} animate={animating} />
                    ))}
                  </div>
                  {riskEntries.length > 1 && (
                    <div className="pia-risk-bottom" style={{ display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'12px' }}>
                      {riskEntries.slice(1).map(([nivel, prob]) => (
                        <RiskCard key={nivel} nivel={nivel} pct={Math.round(prob * 100)} isTop={false} animate={animating} />
                      ))}
                    </div>
                  )}
                </div>

                {/* Params used */}
                <div className="pia-params" style={{ background:'#fff',borderRadius:'14px',padding:'12px 18px',marginTop:'14px',display:'flex',flexWrap:'wrap',gap:'7px',border:'1px solid #f1f5f9' }}>
                  {[
                    { icon:'🏘️', text: municipioIAName || form.municipio,                                                color:'#3b82f6' },
                    { icon: area === 'Urbana' ? '🏙️' : '🌄', text: area,                                              color:'#0ea5e9' },
                    { icon:'📍', text: todosLugares.find(l => String(l.Cod_Lugar) === String(form.lugar))?.Descripcion || form.lugar, color:'#059669' },
                    { icon:'🕐', text:`${String(form.hora).padStart(2,'0')}:00`,                 color:'#8b5cf6' },
                    { icon:'📅', text: DIAS[form.dia_semana],                                    color:'#0ea5e9' },
                    { icon:'🗓️', text: MESES[form.mes - 1],                                     color:'#f59e0b' },
                    ...(claseSeleccionada ? [{ icon:'📋', text: claseSeleccionada, color:'#ef4444' }] : []),
                  ].map((c,i) => (
                    <span className="pia-params-tag" key={i} style={{ background:`${c.color}0e`,border:`1px solid ${c.color}28`,borderRadius:'99px',padding:'5px 12px',fontSize:'.78rem',color:c.color,fontWeight:600 }}>
                      {c.icon} {c.text}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── MAPA DE CALOR + HISTORIAL ────────────────────────────────────── */}
        {historial.length > 0 && (
          <div style={{ marginTop:'24px',display:'flex',flexDirection:'column',gap:'16px' }}>

            <div className="pia-card" style={{ padding:'18px 24px',display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:'12px' }}>
              <div style={{ display:'flex',alignItems:'center',gap:'12px' }}>
                <div style={{ width:'36px',height:'36px',borderRadius:'10px',background:'linear-gradient(135deg,#ef4444,#b91c1c)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'18px' }}>
                  🗺️
                </div>
                <div>
                  <h2 className="pia-map-title" style={{ margin:0,fontSize:'.98rem',fontWeight:800,color:'#0f172a' }}>Mapa de Calor — Zonas de Riesgo</h2>
                  <p style={{ margin:0,fontSize:'.73rem',color:'#94a3b8' }}>
                    {historial.length} zonas ·{' '}
                    {lugarCoords
                      ? resultado
                        ? <><span style={{color: NIVEL[resultado.riesgo]?.color, fontWeight:700}}>
                            {NIVEL[resultado.riesgo]?.emoji} Predicción: {NIVEL[resultado.riesgo]?.label}
                          </span> · <span style={{color:'#64748b'}}>{lugarCoords.nombre}</span></>
                        : <><span style={{color:'#2563eb',fontWeight:700}}>📍 {lugarCoords.nombre}</span> · Zoom: aldea</>
                      : rankingSelected
                        ? <><span style={{color:'#7c3aed',fontWeight:700}}>📌 {rankingSelected}</span>
                            {resultado && <span style={{color: NIVEL[resultado.riesgo]?.color, fontWeight:700}}>
                              {' '}· {NIVEL[resultado.riesgo]?.emoji} {NIVEL[resultado.riesgo]?.label}
                            </span>}
                            {!resultado && <> · Haz clic en otra zona</>}</>
                        : 'Haz clic en una zona para seleccionarla · Jalapa, Guatemala'}
                  </p>
                </div>
              </div>
              <div className="pia-map-legend" style={{ display:'flex',gap:'10px',flexWrap:'wrap' }}>
                {[
                  { color:'#ef4444',label:'Alto riesgo'   },
                  { color:'#f97316',label:'Riesgo medio'  },
                  { color:'#f59e0b',label:'Riesgo bajo'   },
                  { color:'#22c55e',label:'Sin historial' },
                  ...(lugarCoords ? [{
                    color: resultado ? (NIVEL[resultado.riesgo]?.color || '#2563eb') : '#2563eb',
                    label: resultado
                      ? `${NIVEL[resultado.riesgo]?.emoji} ${NIVEL[resultado.riesgo]?.label} (aldea)`
                      : 'Aldea selec.',
                  }] : []),
                ].map(l => (
                  <div className="pia-legend-item" key={l.label} style={{ display:'flex',alignItems:'center',gap:'5px',fontSize:'.71rem',color:'#64748b',fontWeight:600 }}>
                    <span style={{ width:'10px',height:'10px',borderRadius:'50%',background:l.color,display:'inline-block' }} />
                    {l.label}
                  </div>
                ))}
              </div>
            </div>

            <div className="pia-map-section" style={{ display:'grid',gridTemplateColumns:'1fr 420px',gap:'16px',alignItems:'start' }}>

              {/* MAPA LEAFLET CON HEATMAP */}
              <div className="pia-card" style={{ overflow:'hidden',height:'420px' }}>
                <MapContainer center={[mapCenter.lat, mapCenter.lon]} zoom={9} style={{ height:'100%',width:'100%' }} scrollWheelZoom={true}>
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <MapCenterController center={mapCenter} zoom={lugarCoords ? 13 : rankingSelected ? 11 : 9} />
                  <HeatmapLayer data={historial} selectedMunicipio={rankingSelected} resultado={resultado} />
                  {lugarCoords && (() => {
                    const pinColor = resultado ? (NIVEL[resultado.riesgo]?.color || '#2563eb') : '#2563eb';
                    const pinLabel = resultado
                      ? `${NIVEL[resultado.riesgo]?.emoji} ${NIVEL[resultado.riesgo]?.label} · ${lugarCoords.nombre}`
                      : `📍 ${lugarCoords.nombre}`;
                    return (
                      <CircleMarker
                        center={[lugarCoords.lat, lugarCoords.lon]}
                        radius={14}
                        pathOptions={{
                          fillColor: pinColor,
                          fillOpacity: 0.95,
                          color: '#fff',
                          weight: 3,
                        }}
                      >
                        <Tooltip permanent direction="top" offset={[0, -16]}
                          className="pia-lugar-tooltip">
                          {pinLabel}
                        </Tooltip>
                      </CircleMarker>
                    );
                  })()}
                </MapContainer>
              </div>

              {/* RANKING DE MUNICIPIOS — clic para seleccionar */}
              <div className="pia-card" style={{ overflow:'hidden' }}>
                <div className="pia-ranking-header" style={{ padding:'13px 18px',borderBottom:'1px solid #f1f5f9',display:'flex',alignItems:'center',justifyContent:'space-between',gap:'8px' }}>
                  <h3 style={{ margin:0,fontSize:'.73rem',fontWeight:700,color:'#64748b',textTransform:'uppercase',letterSpacing:'.09em' }}>
                    {rankingSelected
                      ? <span>📍 {rankingSelected}</span>
                      : 'Ranking de Municipios · Clic para ver'}
                  </h3>
                  {rankingSelected && (
                    <button
                      onClick={() => setRankingSelected('')}
                      style={{ border:'none',background:'#f1f5f9',color:'#64748b',borderRadius:'6px',padding:'3px 10px',fontSize:'.68rem',fontWeight:700,cursor:'pointer' }}
                    >✕ Ver todos</button>
                  )}
                </div>
                <div className="pia-ranking-card" style={{ overflowY:'auto',maxHeight:'380px' }}>
                  {historial.map((z,i) => {
                    const pctZona  = Math.round((z.total_incidentes / maxZona) * 100);
                    const col      = TIPO_COLOR[z.tipo_frecuente] || '#64748b';
                    const heatCol  = pctZona > 66 ? '#ef4444' : pctZona > 33 ? '#f97316' : pctZona > 10 ? '#f59e0b' : '#22c55e';
                    const medal    = i===0 ? '🥇' : i===1 ? '🥈' : i===2 ? '🥉' : null;
                    const selected = rankingSelected === z.municipio;
                    return (
                      <div
                        key={i}
                        className="hist-row pia-ranking-item"
                        onClick={() => setRankingSelected(prev => prev === z.municipio ? '' : z.municipio)}
                        style={{
                          padding:'12px 18px',borderBottom:'1px solid #f8fafc',
                          display:'flex',alignItems:'center',gap:'12px',
                          cursor:'pointer',
                          background: selected ? `${heatCol}12` : undefined,
                          borderLeft: selected ? `4px solid ${heatCol}` : '4px solid transparent',
                          transition:'background 0.2s,border-color 0.2s',
                        }}
                      >
                        <div className="pia-ranking-medal" style={{ width:'28px',textAlign:'center',flexShrink:0 }}>
                          {medal
                            ? <span style={{ fontSize:'1.1rem' }}>{medal}</span>
                            : <span style={{ fontSize:'.78rem',fontWeight:800,color:'#cbd5e1' }}>{i+1}</span>
                          }
                        </div>
                        <div style={{ flex:1,minWidth:0 }}>
                          <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'5px' }}>
                            <span className="pia-ranking-zone" style={{ fontWeight:700,fontSize:'.85rem',color:'#1e293b' }}>{z.municipio}</span>
                            <span className="pia-ranking-cases" style={{ fontSize:'.78rem',fontWeight:800,color:heatCol }}>{z.total_incidentes} casos</span>
                          </div>
                          <div className="pia-ranking-bar" style={{ background:'#f1f5f9',borderRadius:'99px',height:'6px',overflow:'hidden' }}>
                            <div style={{ width:`${pctZona}%`,height:'6px',borderRadius:'99px',background:`linear-gradient(90deg,${heatCol},${heatCol}88)` }} />
                          </div>
                          <div style={{ marginTop:'5px',display:'flex',alignItems:'center',justifyContent:'space-between' }}>
                            <span className="pia-ranking-type" style={{ background:`${col}12`,color:col,border:`1px solid ${col}28`,borderRadius:'6px',padding:'2px 8px',fontSize:'.69rem',fontWeight:700,textTransform:'capitalize' }}>
                              {z.lugar}
                            </span>
                            {selected && (
                              <span style={{ fontSize:'.65rem',color:heatCol,fontWeight:700 }}>✓ Seleccionado</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
